import React, { useEffect, useState, useRef } from 'react';
import { Dropdown, DropdownButton, Form, Modal } from 'react-bootstrap-5';
import { post } from 'helpers/api';
import { serverErrors } from 'helpers/api_errors';
import { Button } from 'components/ui/button';
import { Errors } from 'components/ui/errors';
import LoadingSpinner from 'components/ui/loading_spinner';

import {
  faPaperPlane,
  faPlus,
  faTimes,
  faChevronLeft,
  faChevronDown
} from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import {
  formatDateTime,
  shareClientReport,
  removeShareableToken,
  type ShareableToken,
  type Collaborator
} from './util';

// helpers/api rejects with the response's `errors` when there are any, otherwise with the generic request error,
// which gets this screen's own fallback.
function errorMessages(error: unknown, fallback: string): string[] {
  if (typeof error === 'string') {
    return [error];
  }

  const messages = serverErrors(error);
  if (messages.length > 0 && messages.every(message => typeof message === 'string')) {
    return messages;
  }

  return [fallback];
}

type ShareClientReportModalProps = {
  collaborators: Collaborator[];
  projectId: number;
  shareableTokens: ShareableToken[];
  show: boolean;
  onCloseModal: () => void;
  onTokensChanged?: () => void;
}

function ShareClientReportModal({
  collaborators,
  projectId,
  shareableTokens,
  show,
  onCloseModal,
  onTokensChanged // Add this to the function params
}: ShareClientReportModalProps) {

  const [isAddingEmail, setIsAddingEmail] = useState(false);
  const [email, setEmail] = useState('');
  const [message, setMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [resendingTokens, setResendingTokens] = useState<Set<number>>(new Set());
  const [recentlySent, setRecentlySent] = useState<Set<number>>(new Set());
  const [errors, setErrors] = useState<string[] | null>(null);

  const timeoutRefs = useRef<Set<NodeJS.Timeout>>(new Set());

  useEffect(() => {
    return () => {
      // Clear all timeouts on unmount to prevent memory leaks
      timeoutRefs.current.forEach(timeout => clearTimeout(timeout));
      timeoutRefs.current.clear();
    };
  }, []);

  const [selectedCollaborator, setSelectedCollaborator] = useState(
    collaborators?.length > 0 ? collaborators[0].id : ''
  );
  const [selectedCollaboratorName, setSelectedCollaboratorName] = useState(
    collaborators?.length > 0 ? collaborators[0].name : ''
  );

  useEffect(() => {
    if (collaborators?.length > 0 && !selectedCollaborator) {
      setSelectedCollaborator(collaborators[0].id.toString());
      setSelectedCollaboratorName(collaborators[0].name);
    }
  }, [collaborators]);

  const toggleAddingEmail = (adding: boolean) => {
    setErrors(null);
    setIsAddingEmail(adding);
  };

  const handleClose = () => {
    setErrors(null);
    onCloseModal();
  };

  const handleCollaboratorSelect = (collabId: number, collabName: string) => {
    setSelectedCollaborator(collabId);
    setSelectedCollaboratorName(collabName);
  };

  const sendShareClientReport = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setErrors(null);

    try {
      await shareClientReport(
        projectId,
        selectedCollaborator,
        email,
        message
      );

      setEmail('');
      setMessage('');
      setIsAddingEmail(false);

      if (onTokensChanged) {
        onTokensChanged();
      }
    } catch (error) {
      setErrors(errorMessages(error, "The invitation wasn't sent."));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleRemoveToken = async (e, tokenId: number) => {
    e.preventDefault();
    setErrors(null);

    try {
      await removeShareableToken(projectId, tokenId);

      if (onTokensChanged) {
        onTokensChanged();
      }
    } catch (error) {
      setErrors(errorMessages(error, "The email wasn't removed."));
    }
  };

  const downloadPDF = async () => {
    const pdfPath = `/projects/${projectId}/client_report`;
    setErrors(null);

    try {
      const data = await post(pdfPath);
      window.location.assign(data.editPath);
    } catch (error) {
      setErrors(errorMessages(error, "The PDF wasn't created."));
    }
  };

  const handleResendToken = async (e, token: ShareableToken) => {
    e.preventDefault();

    if (resendingTokens.has(token.id)) {
      return;
    }

    setResendingTokens(prev => new Set(prev).add(token.id));
    setErrors(null);

    try {
      await shareClientReport(
        projectId,
        collaborators[0].id,
        token.email,
        ''
      );

      setRecentlySent(prev => new Set(prev).add(token.id));

      const timeOutId = setTimeout(() => {
        setRecentlySent((prev) => {
          const newSet = new Set(prev);
          newSet.delete(token.id);
          return newSet;
        });
        timeoutRefs.current.delete(timeOutId);
      }, 3000);

      timeoutRefs.current.add(timeOutId);

      if (onTokensChanged) {
        onTokensChanged();
      }
    } catch (error) {
      setErrors(errorMessages(error, "The invitation wasn't sent."));
    } finally {
      setResendingTokens((prev)  => {
        const newSet = new Set(prev);
        newSet.delete(token.id);
        return newSet;
      });
    }
  };

  const isTokenDisabled = (tokenId: number) => {
    return resendingTokens.has(tokenId) || recentlySent.has(tokenId);
  };

  const renderResendStatus = (token: ShareableToken) => {
    const isResending = resendingTokens.has(token.id);
    const wasRecentlySent = recentlySent.has(token.id);

    if (isResending) {
      return (
        <>
          <LoadingSpinner flex size="md" skipTopBuffer />
          Sending...
        </>
      );
    }

    if (wasRecentlySent) {
      return (
        <span className="ms-2 text-success">
          Sent successfully!
        </span>
      );
    }

    return (
      <a
        className="clickable btn-link ml-2"
        onClick={isTokenDisabled(token.id) ? undefined : e => handleResendToken(e, token)}
      >
        Resend
      </a>
    );
  };

  const renderTokenEmail = (token: ShareableToken) => {
    if (!token.enabled) {
      return null;
    }

    return (
      <div key={token.id} >
        <div className="d-flex align-items-center w-100 gap-2">
          <Form.Control
            className="flex-grow-1 me-3"
            readOnly
            type="email"
            value={token.email || ''}
          />
          <Button icon={faTimes} label="Remove" variant="destructive" onClick={e => handleRemoveToken(e, token.id)}/>
        </div>
        <div className="mt-1 d-flex align-items-center gap-4">
          <div className="text-muted">
            Last Sent At: {token.lastSent ? formatDateTime(token.lastSent) : 'Not sent yet'}
          </div>
          {renderResendStatus(token)}
        </div>
      </div>
    );
  };

  const renderAddEmailForm = () => {
    return (
      <div className="w-100 p-4">
        <div className="mb-4">
          <Button
            icon={faChevronLeft}
            variant="secondary"
            onClick={() => toggleAddingEmail(false)}
          >
            <span className="mx-2">Back</span>
          </Button>
        </div>

        <Errors className="mb-4" errors={errors} />

        <Form onSubmit={sendShareClientReport}>
          <div className="row mb-4">
            <div className="col-md-6">
              <Form.Group>
                <Form.Label className="mb-1">Email Address</Form.Label>
                <Form.Control
                  placeholder="Enter Email Address"
                  required
                  type="email"
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                />
              </Form.Group>
            </div>
            <div className="col-md-6">
              <Form.Group className="form-group-wrapper">
                <Form.Label className="mb-1">From</Form.Label>
                <DropdownButton
                  title={
                    <>
                      {selectedCollaboratorName}
                      <FontAwesomeIcon icon={faChevronDown} />
                    </>
                  }
                >
                  {collaborators.map(collab => (
                    <Dropdown.Item
                      key={collab.id}
                      onClick={() => handleCollaboratorSelect(collab.id, collab.name)}
                    >
                      {collab.name}
                    </Dropdown.Item>
                  ))}
                </DropdownButton>
              </Form.Group>
            </div>
          </div>

          <Form.Group className="mb-4">
            <Form.Label className="mb-1">Message</Form.Label>
            <Form.Control
              as="textarea"
              placeholder="Message"
              rows={6}
              value={message}
              onChange={e => setMessage(e.target.value)}
            />
          </Form.Group>

          <div className="d-flex justify-content-end">
            <Button label="Cancel" type="button" variant="ghost" onClick={() => toggleAddingEmail(false)}/>
            <Button disabled={!email || isSubmitting} label="Share" type="submit"/>
          </div>
        </Form>
      </div>
    );
  };

  const renderShareView = () => {
    return (
      <>
        <Modal.Header>
          <Modal.Title className="d-flex gap-2 align-items-center">
            <FontAwesomeIcon icon={faPaperPlane} />
            <div>
              Share the Report
              <div className="fs-body fw-normal">Send anyone an invitation to view your Client Report.</div>
            </div>
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="body d-flex flex-column w-100 gap-4">
            <Errors errors={errors} />
            {(shareableTokens || []).map(token => renderTokenEmail(token))}
          </div>
        </Modal.Body>
        <Modal.Footer className="d-flex flex-row justify-content-between w-100">
          <div className="pl-5">
            <Button
              icon={faPlus}
              variant="secondary"
              onClick={() => toggleAddingEmail(true)}
            >
              <span className="mx-2">Add Email</span>
            </Button>
          </div>
          <div className="pr-5">
            <Button label="Download PDF" onClick={downloadPDF}/>
          </div>
        </Modal.Footer>
      </>
    );
  };

  const renderAddEmailView = () => {
    return (
      <Modal.Body>
        {renderAddEmailForm()}
      </Modal.Body>
    );
  };

  return (
    <Modal show={show} size={isAddingEmail ? 'lg' : undefined} onHide={handleClose}>
      {isAddingEmail ? renderAddEmailView() : renderShareView()}
    </Modal>
  );
}

export default ShareClientReportModal;
